/**
 * Event Tracking API client (POST /logs/events).
 *
 * Events are queued in memory and flushed in batches: every FLUSH_INTERVAL_MS,
 * when the queue reaches MAX_BATCH, and with fetch keepalive when the page is
 * hidden/closed (sendBeacon can't carry the x-api-key header).
 *
 * Usage:
 *   trackEvent("modal_shown", { label: "visa_verification_modal" });
 *   trackEvent("modal_closed", { label: "visa_verification_modal", metadata: { durationMs: 4200 } });
 *
 * initEventTracking() must run once after tconfig is loaded (AppWrapper does
 * this); trackEvent() calls made earlier are queued and sent after init.
 */
import { getLocalStorageToken } from "../App/AppWrapper/localStorages";

export type TrackEventName =
  | "modal_shown"
  | "modal_opened"
  | "modal_closed"
  | "first_visit"
  | "return_visit"
  | "session_start"
  | "session_end"
  | "page_view"
  | "click"
  // Visa verification funnel (MB-1727)
  | "verification_modal_shown"
  | "verification_continue_clicked"
  | "verification_cancel_clicked"
  | "verification_returned";

type TrackEventCategory = "modal" | "session" | "page" | "interaction";

// category/name pairs are validated by the backend; deriving the category here
// makes an invalid combination unrepresentable at call sites.
const CATEGORY_BY_NAME: Record<TrackEventName, TrackEventCategory> = {
  modal_shown: "modal",
  modal_opened: "modal",
  modal_closed: "modal",
  first_visit: "session",
  return_visit: "session",
  session_start: "session",
  session_end: "session",
  page_view: "page",
  click: "interaction",
  verification_modal_shown: "modal",
  verification_continue_clicked: "interaction",
  verification_cancel_clicked: "interaction",
  verification_returned: "interaction",
};

export interface TrackEventOptions {
  label?: string;
  metadata?: Record<string, unknown>;
}

interface TrackedEvent {
  category: TrackEventCategory;
  name: TrackEventName;
  sessionId: string;
  platform: "web";
  clientTs: string;
  label?: string;
  metadata?: Record<string, unknown>;
  /**
   * Auth token captured when the event was queued, so the event is attributed
   * to the user who was logged in at that moment even if a logout happens
   * before the batch is flushed (e.g. the modal's "Not now"). Stripped from
   * the payload before sending — it travels as the Authorization header.
   */
  _auth?: string;
}

const FLUSH_INTERVAL_MS = 7000;
const MAX_BATCH = 50;
const SESSION_ID_STORAGE_KEY = "tripian_event_session_id";
const VISITED_STORAGE_KEY = "tripian_event_visited";

let queue: TrackedEvent[] = [];
let initialized = false;
let flushTimer: ReturnType<typeof setInterval> | undefined;

const generateUuid = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const getSessionId = (): { sessionId: string; isNew: boolean } => {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
    if (existing) return { sessionId: existing, isNew: false };
    const sessionId = generateUuid();
    sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
    return { sessionId, isNew: true };
  } catch {
    return { sessionId: generateUuid(), isNew: true };
  }
};

export const trackEvent = (
  name: TrackEventName,
  options: TrackEventOptions = {}
): void => {
  queue.push({
    category: CATEGORY_BY_NAME[name],
    name,
    sessionId: getSessionId().sessionId,
    platform: "web",
    clientTs: new Date().toISOString(),
    ...(options.label ? { label: options.label } : {}),
    ...(options.metadata ? { metadata: options.metadata } : {}),
    _auth: getLocalStorageToken()?.idToken,
  });

  if (initialized && queue.length >= MAX_BATCH) {
    flush();
  }
};

const flush = async (keepalive = false): Promise<void> => {
  if (!initialized || queue.length === 0) return;
  if (!window.tconfig?.TRIPIAN_API_URL || !window.tconfig?.TRIPIAN_API_KEY)
    return;

  // The Authorization header is per-request, but each event carries the token
  // captured at queue time — so group events by token and send one request per
  // group. Usually that's a single group.
  const pending = queue.splice(0, queue.length);
  const groups = new Map<string | undefined, TrackedEvent[]>();
  pending.forEach(event => {
    const group = groups.get(event._auth);
    if (group) group.push(event);
    else groups.set(event._auth, [event]);
  });

  for (const [idToken, events] of Array.from(groups.entries())) {
    for (let i = 0; i < events.length; i += MAX_BATCH) {
      const chunk = events.slice(i, i + MAX_BATCH);
      try {
        // eslint-disable-next-line no-await-in-loop
        await fetch(`${window.tconfig.TRIPIAN_API_URL}/logs/events`, {
          method: "POST",
          keepalive,
          headers: {
            "x-api-key": window.tconfig.TRIPIAN_API_KEY,
            "Content-Type": "application/json",
            ...(idToken ? { Authorization: idToken } : {}),
          },
          body: JSON.stringify({
            events: chunk.map(({ _auth, ...event }) => event),
          }),
        });
      } catch {
        // Network error: put the chunk back so the next flush retries it.
        // A 4xx response is not retried — the whole batch would be rejected again.
        queue.unshift(...chunk);
      }
    }
  }
};

/**
 * Detects first vs. return visit via a localStorage flag. Exposed as a helper
 * so callers can decide when (and whether) to send the visit events.
 * Returns "first" only once per browser; "unknown" if localStorage is blocked.
 */
export const detectVisitType = (): "first" | "return" | "unknown" => {
  try {
    if (localStorage.getItem(VISITED_STORAGE_KEY)) return "return";
    localStorage.setItem(VISITED_STORAGE_KEY, new Date().toISOString());
    return "first";
  } catch {
    return "unknown";
  }
};

/**
 * Starts the flush loop and the page-hide flush listeners. Emits nothing by
 * itself — events are sent only where trackEvent() is explicitly called.
 * Requires window.tconfig to be populated (call after setConfigList resolves).
 */
export const initEventTracking = (): void => {
  if (initialized) return;
  initialized = true;

  flushTimer = setInterval(() => {
    flush();
  }, FLUSH_INTERVAL_MS);

  // Flush what's left whenever the page goes to the background or closes
  // (sendBeacon can't carry the x-api-key header, hence fetch keepalive).
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush(true);
    }
  });

  window.addEventListener("pagehide", () => {
    flush(true);
  });
};

export const stopEventTracking = (): void => {
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = undefined;
  initialized = false;
  queue = [];
};
